import { create } from "zustand";

export type TaskStatusFilter = "all" | "enabled" | "paused" | "archived";
export type TaskTypeFilter = "all" | "scheduled" | "manual" | "triggered";
export type TaskSortOption = "last-run" | "next-run" | "name-az" | "name-za";
export type TaskViewMode = "list" | "kanban";

const VIEW_MODE_KEY = "teammate-ui-viewMode";

/** Read persisted view mode, falling back to "list". */
function loadViewMode(): TaskViewMode {
	try {
		const stored = localStorage.getItem(VIEW_MODE_KEY);
		return stored === "list" ? "list" : "kanban";
	} catch {
		return "list";
	}
}

interface TeammateUIState {
	// Search and filters
	searchQuery: string;
	statusFilter: TaskStatusFilter;
	typeFilter: TaskTypeFilter;
	sortOption: TaskSortOption;
	viewMode: TaskViewMode;
	kanbanHideUnassigned: boolean;

	// Task run panel
	selectedTaskId: number | null;
	isPanelOpen: boolean;

	// Run preview panel
	previewRunId: number | null;
	isRunPreviewOpen: boolean;

	// Actions
	setSearchQuery: (query: string) => void;
	setStatusFilter: (filter: TaskStatusFilter) => void;
	setTypeFilter: (filter: TaskTypeFilter) => void;
	setSortOption: (option: TaskSortOption) => void;
	setViewMode: (mode: TaskViewMode) => void;
	setKanbanHideUnassigned: (hide: boolean) => void;
	setSelectedTask: (taskId: number | null) => void;
	closePanel: () => void;
	openRunPreview: (runId: number) => void;
	closeRunPreview: () => void;
	clearFilters: () => void;
	reset: () => void;
}

export const useTeammateUIStore = create<TeammateUIState>((set) => ({
	searchQuery: "",
	statusFilter: "enabled",
	typeFilter: "all",
	sortOption: "last-run",
	viewMode: loadViewMode(),
	kanbanHideUnassigned: true,
	selectedTaskId: null,
	isPanelOpen: false,
	previewRunId: null,
	isRunPreviewOpen: false,

	setSearchQuery: (query) => set({ searchQuery: query }),
	setStatusFilter: (filter) => set({ statusFilter: filter }),
	setTypeFilter: (filter) => set({ typeFilter: filter }),
	setSortOption: (option) => set({ sortOption: option }),
	setViewMode: (mode) => {
		try {
			localStorage.setItem(VIEW_MODE_KEY, mode);
		} catch {
			/* noop */
		}
		set({ viewMode: mode });
	},
	setKanbanHideUnassigned: (hide) => set({ kanbanHideUnassigned: hide }),

	setSelectedTask: (taskId) => set({ selectedTaskId: taskId, isPanelOpen: taskId !== null }),

	closePanel: () => set({ isPanelOpen: false, selectedTaskId: null }),

	openRunPreview: (runId) =>
		set({ previewRunId: runId, isRunPreviewOpen: true, isPanelOpen: false, selectedTaskId: null }),
	closeRunPreview: () => set({ isRunPreviewOpen: false, previewRunId: null }),

	clearFilters: () =>
		set({
			searchQuery: "",
			statusFilter: "enabled",
			typeFilter: "all",
			sortOption: "last-run",
		}),

	reset: () => {
		try {
			localStorage.removeItem(VIEW_MODE_KEY);
		} catch {
			/* noop */
		}
		set({
			searchQuery: "",
			statusFilter: "enabled",
			typeFilter: "all",
			sortOption: "last-run",
			viewMode: "kanban",
			kanbanHideUnassigned: true,
			selectedTaskId: null,
			isPanelOpen: false,
			previewRunId: null,
			isRunPreviewOpen: false,
		});
	},
}));
